import { Folder } from '@anm/api/modules/videoFolder';
import BreadcrumbItem, { BreadcrumbItemProps } from '@anm/components/Breadcrumbs/BreadcrumbItem';
import DropDownBreadcrumb from '@anm/components/DropDownBreadcrumb';
import { DropZone } from '@anm/components/drag-drop';
import { DragDropContext } from '@anm/contexts/DragOverContext';
import { RouterContext } from '@anm/contexts/RouterContext';
import cutFileExtension from '@anm/helpers/cutFileExtension';
import { RouteParams } from 'next-routes';
import { useCallback, useContext, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import vtConfig from '../../../config';
import routes from '../../../routes';
import { videoFolderActions, videoFolderSelectors } from '../../../store/modules/videoFolder';
import { ProjectsDragDropPayload } from '../ProjectFolderBlock';
import { isNotFoundErrorCode } from '../VideoFolders/helpers';

import MyVideosTitle from './MyVideosTitle';
import { uploadsActions } from '@anm/store/modules/uploads';

const {
  uploadVideoDefaults: { metaInfo }
} = vtConfig();

const { Router } = routes;

const DropZoneWrapper: typeof DropZone = styled(DropZone)`
  transform: scale(1.3, 1.9);
  border-radius: 3px;
`;

const ProjectBreadcrumbs: FC = () => {
  const dispatch = useDispatch();
  const videoFolder = useSelector(videoFolderSelectors.selectVideoFolders);
  const folderTree = useSelector(videoFolderSelectors.selectFolderTree);
  const makeFolderTreeListByPath = useSelector(videoFolderSelectors.makeFolderTreeListByPath);
  const { params } = useContext(RouterContext);
  const { isDragOver, setIsDragOver } = useContext(DragDropContext);

  const handleClick = useCallback(
    ({ path }: Folder) => {
      const routeName = path ? 'my-projects-folder' : 'my-projects';
      Router.pushRoute(routeName, {
        ...((path && { folderPath: path.replace('/', '') }) as RouteParams)
      });
    },
    [Router]
  );

  const handleUploadFile = useCallback((file: File, folderId: number) => {
    const name = cutFileExtension(file?.name);
    const description = `${name} - ${metaInfo.description}`;

    dispatch(
      uploadsActions.createUpload({
        user: null,
        isUploadFile: true,
        uploads: [
          {
            file,
            folderId,
            meta: { name, description }
          }
        ]
      })
    );
  }, []);

  const moveFolder = useCallback(
    (folderId: number, parentId: number) =>
      dispatch(
        videoFolderActions.moveFolder({
          folderId,
          toParentId: parentId
        })
      ),
    []
  );

  const moveProject = useCallback(
    (folderId: number, projectId: string) =>
      dispatch(
        videoFolderActions.moveProject({
          folderId,
          projectId
        })
      ),
    []
  );

  const handleDropped = useCallback(
    (item: BreadcrumbItemProps) => (payload: ProjectsDragDropPayload) => {
      setIsDragOver(false);

      // file && handleUploadFile(file, item.id as number); TODO need to save somewhere project which will created from drag&drop
      payload?.folderId && moveFolder(payload?.folderId, item.id as number);
      payload?.projectId && moveProject(item.id as number, payload.projectId);
    },
    [handleUploadFile, moveFolder, moveProject]
  );

  if (!folderTree || isNotFoundErrorCode(videoFolder.error!)) {
    return null;
  }

  return (
    <>
      {!params.folderPath ? (
        <MyVideosTitle />
      ) : (
        <DropDownBreadcrumb<Folder>
          data={makeFolderTreeListByPath(params.folderPath)}
          onClick={handleClick}
          renderItem={item => (
            <DropZoneWrapper<ProjectsDragDropPayload>
              canDrop={() => videoFolder.data?.id !== (item.id as number)}
              canShowDropZone={isDragOver}
              onDropped={handleDropped(item)}
            >
              <BreadcrumbItem {...item} />
            </DropZoneWrapper>
          )}
        />
      )}
    </>
  );
};

export default ProjectBreadcrumbs;
